import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { FaEye, FaDownload, FaEdit, FaTrash, FaFilePdf } from "react-icons/fa";
import { getClients } from "../../../../services/global_redux/globale_actions/Client.action";
import { getUnits } from "../../../../services/global_redux/globale_actions/Unit.action";
import {
  getSnECertificates,
  uploadSnECertificate,
  updateSnECertificate,
  deleteSnECertificate,
  downloadSnECertificate,
  viewSnECertificate,
  getCertificateTypes,
  createCertificateType,
} from "../../../../services/register_redux/register_actions/SnECertificate.action";
import file_upload_icon from "../../../../assets/reUsableTemplates_assets/file_upload_icon.png";
import file_uploaded_icon from "../../../../assets/reUsableTemplates_assets/file_uploaded_icon.png";
import into_icon from "../../../../assets/global_assets/create_data_imgs/into_icon.png";
import GeneratedFormPdfPreview from "../Generated_Forms/GeneratedFormPdfPreview";
import CreateCertificateTemplate from "../../../global/Settings/modalForm/CreateCertificateTemplate";
import "./SnECertificate.css";



const formatTypeLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getLatestForUnit = (rows, unitId) =>
  rows.find((row) => row.unit_id === unitId && row.is_latest) ||
  rows
    .filter((row) => row.unit_id === unitId)
    .sort((a, b) => {
      const dateA = a.validity_expire_date || "";
      const dateB = b.validity_expire_date || "";
      if (dateA !== dateB) return dateB.localeCompare(dateA);
      return (b.id || 0) - (a.id || 0);
    })[0];

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const SnECertificate = ({ showSnackbar, setLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { certType: certTypeParam } = useParams();

  const [certificateTypeList, setCertificateTypeList] = useState([]);
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [clientID, setClientID] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [validityExpireDate, setValidityExpireDate] = useState("");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");

  const activeTypeKey = useMemo(() => {
    if (!typesLoaded || !certificateTypeList.length) return certTypeParam || "";
    const match = certificateTypeList.find(
      (t) => t.certificate_type === certTypeParam
    );
    return match ? match.certificate_type : certificateTypeList[0].certificate_type;
  }, [certificateTypeList, certTypeParam, typesLoaded]);

  const activeTypeLabel = useMemo(
    () => formatTypeLabel(activeTypeKey),
    [activeTypeKey]
  );

  const loadCertificateTypes = useCallback(async () => {
    const result = await dispatch(getCertificateTypes());
    if (result?.success && Array.isArray(result.data)) {
      setCertificateTypeList(result.data);
      setTypesLoaded(true);
      return result.data;
    }
    setCertificateTypeList([]);
    setTypesLoaded(true);
    return [];
  }, [dispatch]);

  useEffect(() => {
    loadCertificateTypes();
  }, [loadCertificateTypes]);

  useEffect(() => {
    if (!typesLoaded) return;
    if (!certificateTypeList.length) {
      if (certTypeParam) {
        navigate("/certificate", { replace: true });
      }
      return;
    }
    const exists = certificateTypeList.some(
      (t) => t.certificate_type === certTypeParam
    );
    if (!exists) {
      navigate(`/certificate/${certificateTypeList[0].certificate_type}`, {
        replace: true,
      });
    }
  }, [typesLoaded, certificateTypeList, certTypeParam, navigate]);

  const latestForSelectedUnit = useMemo(
    () => (selectedUnit ? getLatestForUnit(certificates, selectedUnit) : null),
    [certificates, selectedUnit]
  );

  const stats = useMemo(() => {
    const latestRows = certificates.filter((c) => c.is_latest);
    return {
      total: certificates.length,
      branches: new Set(certificates.map((c) => c.unit_id)).size,
      valid: latestRows.filter((c) => c.status_label === "Valid").length,
      expired: latestRows.filter((c) => c.status_label === "Expired").length,
    };
  }, [certificates]);

  const editingRow = useMemo(
    () => certificates.find((c) => c.id === editingId) || null,
    [certificates, editingId]
  );

  const loadClients = useCallback(async () => {
    const result = await dispatch(getClients());
    if (result?.success && Array.isArray(result.data)) {
      setClientOptions(result.data);
    }
  }, [dispatch]);

  const loadCertificates = useCallback(async () => {
    if (!clientID || !activeTypeKey) {
      setCertificates([]);
      return;
    }
    setLoading(true);
    try {
      const result = await dispatch(getSnECertificates(clientID, activeTypeKey));
      if (result.success) {
        setCertificates(result.data || []);
      } else {
        setCertificates([]);
      }
    } finally {
      setLoading(false);
    }
  }, [clientID, dispatch, setLoading, activeTypeKey]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  useEffect(() => {
    resetForm();
    setSelectedUnit("");
    setUnitOptions([]);
  }, [activeTypeKey]);

  const handleCertificateTypeChange = (e) => {
    const nextType = e.target.value;
    if (nextType && nextType !== activeTypeKey) {
      navigate(`/certificate/${nextType}`);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setValidityExpireDate("");
    resetFile();
  };

  const handleClientChange = async (e) => {
    const value = e.target.value;
    setClientID(value);
    setSelectedUnit("");
    setUnitOptions([]);
    resetForm();
    if (value) {
      const result = await dispatch(getUnits(value));
      if (result?.success && Array.isArray(result.data)) {
        setUnitOptions(result.data);
      }
    }
  };

  const handleUnitChange = (e) => {
    setSelectedUnit(e.target.value);
    if (!editingId) {
      setValidityExpireDate("");
      resetFile();
    }
  };

  const resetFile = () => {
    setFile(null);
    const input = document.getElementById("sneFileInput");
    if (input) input.value = "";
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile.name.split(".").pop()?.toLowerCase();
    if (ext !== "pdf") {
      showSnackbar("Only PDF files are allowed.", "error");
      return;
    }
    setFile({ name: selectedFile.name, raw: selectedFile });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileSelect(e.dataTransfer?.files?.[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!activeTypeKey) {
      showSnackbar("Please create/select a certificate type first.", "error");
      return;
    }
    if (!clientID) {
      showSnackbar("Please select an organization.", "error");
      return;
    }
    if (!selectedUnit) {
      showSnackbar("Please select a branch.", "error");
      return;
    }
    if (!validityExpireDate) {
      showSnackbar("Please enter validity expire date.", "error");
      return;
    }
    if (!editingId && !file?.raw) {
      showSnackbar("Please select a PDF file to upload.", "error");
      return;
    }

    setLoading(true);
    try {
      let result;
      if (editingId) {
        result = await dispatch(
          updateSnECertificate(editingId, {
            file: file?.raw || null,
            validity_expire_date: validityExpireDate,
          })
        );
      } else {
        result = await dispatch(
          uploadSnECertificate({
            file: file.raw,
            client_id: clientID,
            unit_id: selectedUnit,
            validity_expire_date: validityExpireDate,
            certificate_type: activeTypeKey,
          })
        );
      }

      if (result.success) {
        showSnackbar(
          typeof result.message === "string"
            ? result.message
            : editingId
            ? "Certificate updated successfully."
            : "Renewal certificate uploaded successfully.",
          "success"
        );
        resetForm();
        await loadCertificates();
      } else {
        showSnackbar(result.message || "Upload failed.", "error");
      }
    } catch {
      showSnackbar("Upload failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id) => {
    setLoading(true);
    try {
      const result = await dispatch(downloadSnECertificate(id));
      if (result.success) {
        showSnackbar("Download started.", "success");
      } else {
        showSnackbar(result.message || "Download failed.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (row) => {
    setLoading(true);
    try {
      const result = await dispatch(viewSnECertificate(row.id));
      if (result.success && result.blob) {
        if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
        const url = URL.createObjectURL(
          new Blob([result.blob], { type: "application/pdf" })
        );
        setPreviewTitle(row.file_name || `${activeTypeLabel} - ${row.unit_id}`);
        setPreviewPdfUrl(url);
      } else {
        showSnackbar(result.message || "View failed.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const closePreview = () => {
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    setPreviewPdfUrl(null);
    setPreviewTitle("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${activeTypeLabel} record?`)) {
      return;
    }
    setLoading(true);
    try {
      const result = await dispatch(deleteSnECertificate(id));
      if (result.success) {
        showSnackbar(result.message || "Deleted successfully.", "success");
        if (editingId === id) {
          resetForm();
          setSelectedUnit("");
        }
        await loadCertificates();
      } else {
        showSnackbar(result.message || "Delete failed.", "error");
      }
    } finally {
      setLoading(false);
    }
  };


  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const name = (newTypeName || "").trim();
    if (!name) {
      showSnackbar("Please enter a certificate type name.", "warning");
      return;
    }
    setLoading(true);
    try {
      const result = await dispatch(createCertificateType(name));
      if (result.success) {
        showSnackbar(result.message || "Created successfully.", "success");
        setOpenCreate(false);
        setNewTypeName("");
        const types = await loadCertificateTypes();
        const createdKey =
          result.data?.certificate_type ||
          types?.find(
            (t) =>
              formatTypeLabel(t.certificate_type).toLowerCase() ===
              name.toLowerCase()
          )?.certificate_type ||
          types?.[types.length - 1]?.certificate_type;
        if (createdKey) {
          navigate(`/certificate/${createdKey}`, { replace: true });
        }
      } else {
        showSnackbar(result.message || "Create failed.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreateTypePage = () => {
    setNewTypeName("");
    setOpenCreate(true);
  };

  const openViewTypePage = () => {
    navigate("/certificate-types");
  };

  const handleEditRow = (row) => {
    setSelectedUnit(row.unit_id);
    setValidityExpireDate(row.validity_expire_date || "");
    setEditingId(row.id);
    resetFile();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderStatusPill = (row) => {
    if (!row.is_latest) {
      return <span className="sne-status-pill previous">Previous</span>;
    }
    if (row.status_label === "Expired") {
      return <span className="sne-status-pill expired">Latest · Expired</span>;
    }
    if (row.status_label === "Valid") {
      return <span className="sne-status-pill valid">Latest · Valid</span>;
    }
    return <span className="sne-status-pill latest">Latest</span>;
  };

  const uploadLabel = editingId
    ? "Save changes"
    : latestForSelectedUnit
    ? "Upload renewal"
    : "Upload certificate";

  return (
    <div className="container-fluid sne-certificate-page">
 

      <div className="card sne-main-card">
        <div className="card-body">
          <div className="sne-card-header">
          <div className="sne-page-header">
              <h2>Certificate</h2>
              {/* <p>
                Manage branch certificates by organization and type. Renewal uploads are stored as
                history  the certificate with the latest validity date is treated as active.
              </p> */}
            </div>
            <h1>
              {editingId
                ? `Edit ${activeTypeLabel}`
                : activeTypeKey
                ? `Upload ${activeTypeLabel}`
                : "Upload Certificate"}
            </h1>
            <p className="sne-card-subtitle">
              {editingId
                ? `Update the selected ${activeTypeLabel.toLowerCase()} record. Leave the file empty to keep the existing PDF.`
                : `Select organization and branch, set the validity expire date, and upload the PDF.`}
            </p>
          </div>

          {clientID && certificates.length > 0 && (
            <div className="sne-stats-row">
              <div className="sne-stat-card">
                <div className="stat-label">Total records</div>
                <div className="stat-value">{stats.total}</div>
              </div>
              <div className="sne-stat-card">
                <div className="stat-label">Branches</div>
                <div className="stat-value">{stats.branches}</div>
              </div>
              <div className="sne-stat-card valid">
                <div className="stat-label">Active valid</div>
                <div className="stat-value">{stats.valid}</div>
              </div>
              <div className="sne-stat-card expired">
                <div className="stat-label">Active expired</div>
                <div className="stat-value">{stats.expired}</div>
              </div>
            </div>
          )}

          <div className="sne-form-panel">
            <form onSubmit={handleSubmit}>
              {editingId && editingRow && (
                <div className="sne-edit-banner">
                  <span>
                    Editing <strong>{editingRow.file_name || "certificate"}</strong> for branch{" "}
                    <strong>{editingRow.unit_id}</strong>
                  </span>
                  <button type="button" onClick={() => handleView(editingRow)}>
                    Preview PDF
                  </button>
                </div>
              )}

              <div className="sne-section-title">
                <p>Certificate type</p>

                <div className="certificate-header-right">
                  <label className="form-certificate">
                    Create Certificate
                  </label>

                  <div className="certificate-action-buttons">
                    <button
                      type="button"
                      className="certificate-btn create-btn"
                      onClick={openCreateTypePage}
                    >
                      Create
                    </button>

                    <button
                      type="button"
                      className="certificate-btn view-btn"
                      onClick={openViewTypePage}
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>

              <div className="sne-form-grid">
                <div className="sne-field-group">
                  <label className="form-label">
                    Certificate <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={activeTypeKey || ""}
                    onChange={handleCertificateTypeChange}
                    disabled={!!editingId || !certificateTypeList.length}
                  >
                    {!certificateTypeList.length && (
                      <option value="">No certificate types — click Create / View</option>
                    )}
                    {certificateTypeList.map((t) => (
                      <option key={t.id} value={t.certificate_type}>
                        {formatTypeLabel(t.certificate_type)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <p className="sne-section-title">Organization details</p>
              <div className="sne-form-grid">
                <div className="sne-field-group">
                  <label className="form-label">
                    Organization <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={clientID}
                    onChange={handleClientChange}
                  >
                    <option value="">Select organization</option>
                    {clientOptions.map((c) => (
                      <option key={c.client_id} value={c.client_id}>
                        {c.client_name ? `${c.client_name} (${c.client_id})` : c.client_id}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sne-field-group">
                  <label className="form-label">
                    Branch <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={selectedUnit}
                    onChange={handleUnitChange}
                    disabled={!clientID || !!editingId}
                  >
                    <option value="">Select branch</option>
                    {unitOptions.map((unit) => (
                      <option key={unit.unit_id} value={unit.unit_id}>
                        {unit.unit_id}
                      </option>
                    ))}
                  </select>
                  {selectedUnit && latestForSelectedUnit && !editingId && (
                    <div className="sne-info-callout">
                      Current active certificate for this branch expires on{" "}
                      <strong>{formatDisplayDate(latestForSelectedUnit.validity_expire_date)}</strong>.
                      Your upload will be saved as a new renewal record.
                    </div>
                  )}
                </div>

                <div className="sne-field-group">
                  <label className="form-label">
                    Validity expire date <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="date"
                    value={validityExpireDate}
                    onChange={(e) => setValidityExpireDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="sne-upload-section">
                <p className="sne-section-title">Certificate document</p>
                <div className="sne-field-group full-width">
                  <label className="form-label">
                    PDF file {!editingId && <span className="text-danger">*</span>}
                  </label>
                  <div
                    className={`sne-upload-box ${dragActive ? "drag-active" : ""} ${
                      file ? "has-file" : ""
                    }`}
                    onDragOver={(ev) => {
                      ev.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById("sneFileInput").click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        document.getElementById("sneFileInput").click();
                      }
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <img
                        src={file ? file_uploaded_icon : file_upload_icon}
                        alt=""
                        width={40}
                      />
                      <div className="sne-upload-text">
                        <p>
                          {file
                            ? file.name
                            : dragActive
                            ? "Drop your PDF here"
                            : "Drag and drop PDF here, or click to browse"}
                        </p>
                        <small>Supported format: PDF only · Max recommended: 10 MB</small>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="sne-browse-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById("sneFileInput").click();
                      }}
                    >
                      Browse files
                    </button>
                    <input
                      id="sneFileInput"
                      type="file"
                      accept=".pdf,application/pdf"
                      style={{ display: "none" }}
                      onChange={(e) => handleFileSelect(e.target.files?.[0])}
                    />
                  </div>
                </div>
              </div>

              <div className="sne-form-actions">
                {editingId && (
                  <button type="button" className="sne-cancel-btn" onClick={resetForm}>
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="sne-submit-btn"
                  disabled={!activeTypeKey}
                >
                  {uploadLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {clientID && activeTypeKey && (
        <div className="card sne-history-card">
          <div className="card-body">
            <div className="sne-history-header">
              <h5>{activeTypeLabel} history</h5>
              <span>
                {certificates.length} record{certificates.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="sne-history-body">
              {certificates.length === 0 ? (
                <div className="sne-empty-state">
                  <div className="empty-icon">
                    <FaFilePdf />
                  </div>
                  <p>
                    No {activeTypeLabel.toLowerCase()} records uploaded yet for this
                    organization.
                  </p>
                </div>
              ) : (
                <div className="sne-table-wrap">
                  <table className="sne-cert-table">
                    <thead>
                      <tr>
                        <th>Branch</th>
                        <th>File name</th>
                        <th>Validity expires</th>
                        <th>Status</th>
                        <th>Uploaded by</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certificates.map((row) => (
                        <tr key={row.id} className={row.is_latest ? "is-latest" : ""}>
                          <td className="sne-branch-cell">{row.unit_id}</td>
                          <td className="sne-file-cell" title={row.file_name}>
                            {row.file_name || "—"}
                          </td>
                          <td className="sne-date-cell">
                            {formatDisplayDate(row.validity_expire_date)}
                          </td>
                          <td>{renderStatusPill(row)}</td>
                          <td>{row.created_by || row.updated_by || "—"}</td>
                          <td>
                            <div className="sne-action-group">
                              <button
                                type="button"
                                className="sne-icon-btn"
                                title="View"
                                onClick={() => handleView(row)}
                              >
                                <FaEye />
                              </button>
                              <button
                                type="button"
                                className="sne-icon-btn"
                                title="Download"
                                onClick={() => handleDownload(row.id)}
                              >
                                <FaDownload />
                              </button>
                              <button
                                type="button"
                                className="sne-icon-btn"
                                title="Edit"
                                onClick={() => handleEditRow(row)}
                              >
                                <FaEdit />
                              </button>
                              <button
                                type="button"
                                className="sne-icon-btn danger"
                                title="Delete"
                                onClick={() => handleDelete(row.id)}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {previewPdfUrl && (
        <GeneratedFormPdfPreview
          pdfUrl={previewPdfUrl}
          title={previewTitle}
          onClose={closePreview}
        />
      )}
      <CreateCertificateTemplate
        show={openCreate}
        onHide={() => setOpenCreate(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <div className="createCertificateTableMainContainer">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1 mt-2 mx-4">Create Certificate Type</h5>
              <p className="text-muted mb-0 mx-4">
                Create a new certificate type for use in the system.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-transparent btn-sm"
              onClick={() => setOpenCreate(false)}
              aria-label="Close"
            >
              <img src={into_icon} alt="" height={25} />
            </button>
          </div>

          <form onSubmit={handleCreateSubmit}>
            <div className="mb-3 px-3">
              <label className="form-label mx-2">
                <b>Type Name</b> <span className="text-danger">*</span>
              </label>
              <input
                className="form-control mx-2 mb-2"
                type="text"
                placeholder="e.g. S&E Certificate or Trade License"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                autoFocus
                required
              />
              <small className="text-muted mx-2">
                Example:
                <strong> Trade License</strong> → <strong>trade_license</strong>
              </small>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4 mx-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setOpenCreate(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Save
              </button>
            </div>
          </form>
        </div>
      </CreateCertificateTemplate>
    </div>
  );
};

export default SnECertificate;
